import { decodeWav, encodeWav, popSound, meowSound } from './assets.js';
import { soundFromWav } from './sb3.js';
import { ICONS } from './icons.js';

const SFX = [
  ['faster', 'Faster', 'faster'],
  ['slower', 'Slower', 'slower'],
  ['louder', 'Louder', 'louder'],
  ['softer', 'Softer', 'softer'],
  ['mute', 'Mute', 'mute'],
  ['fade-in', 'Fade in', 'fadeIn'],
  ['fade-out', 'Fade out', 'fadeOut'],
  ['reverse', 'Reverse', 'reverse'],
  ['echo', 'Echo', 'echo'],
  ['robot', 'Robot', 'robot'],
  ['trim', 'Trim silence', 'trim'],
  ['undo', 'Undo', 'undo'],
];

export class SoundEditor {
  constructor(app) {
    this.app = app;
    this.canvas = document.getElementById('sound-wave');
    this.ctx = this.canvas.getContext('2d');
    this.samples = new Float32Array(0);
    this.rate = 22050;
    this.undo = [];
    this.source = null;
    this.audio = null;
    this.rec = null;
    this.buildChrome();
    this.bind();
  }

  buildChrome() {
    const transport = document.getElementById('sound-transport');
    if (transport) {
      transport.innerHTML = '';
      for (const [id, title, icon] of [['play', 'Play', 'play'], ['stop', 'Stop', 'stop'], ['record', 'Record', 'record']]) {
        const b = document.createElement('button');
        b.type = 'button';
        b.id = 'sound-' + id;
        b.className = 'icon-btn';
        b.title = title;
        b.innerHTML = ICONS[icon];
        transport.appendChild(b);
      }
    }
    const fx = document.getElementById('sound-effects');
    if (fx) {
      fx.innerHTML = '';
      for (const [id, title, icon] of SFX) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'icon-btn';
        b.title = title;
        b.dataset.sfx = id;
        b.innerHTML = ICONS[icon];
        fx.appendChild(b);
      }
    }
  }

  bind() {
    document.getElementById('sound-play').onclick = () => this.play();
    document.getElementById('sound-stop').onclick = () => this.stop();
    document.getElementById('sound-record').onclick = () => this.record();
    document.getElementById('sound-name').onchange = (e) => {
      const s = this.sound();
      if (s) s.name = e.target.value;
      this.app.renderSoundList();
    };
    document.querySelectorAll('[data-sfx]').forEach((b) => {
      b.onclick = () => this.effect(b.dataset.sfx);
    });
  }

  sound() {
    const t = this.app.current;
    if (!t || !t.sounds.length) return null;
    t._soundIndex = t._soundIndex || 0;
    return t.sounds[t._soundIndex] || t.sounds[0];
  }

  loadSound() {
    const s = this.sound();
    if (!s) {
      this.samples = new Float32Array(0);
      this.draw();
      document.getElementById('sound-name').value = '';
      document.getElementById('sound-info').textContent = 'No sound';
      return;
    }
    document.getElementById('sound-name').value = s.name;
    try {
      if (s._data) {
        const dec = decodeWav(s._data);
        this.samples = dec.samples;
        this.rate = dec.sampleRate;
      } else {
        this.samples = new Float32Array(0);
      }
    } catch {
      this.samples = new Float32Array(0);
    }
    this.draw();
    this.info();
  }

  info() {
    const sec = this.samples.length / this.rate;
    document.getElementById('sound-info').textContent =
      `${this.rate} Hz · ${sec.toFixed(2)}s · ${this.samples.length} samples`;
  }

  draw() {
    const g = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    g.fillStyle = '#1b2330';
    g.fillRect(0, 0, w, h);
    g.strokeStyle = '#cf63cf';
    g.beginPath();
    const n = this.samples.length || 1;
    for (let x = 0; x < w; x++) {
      const i = Math.floor(x * n / w);
      const y = h / 2 - this.samples[i] * (h * 0.42);
      if (x === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
    g.strokeStyle = '#445';
    g.beginPath();
    g.moveTo(0, h / 2);
    g.lineTo(w, h / 2);
    g.stroke();
  }

  ensureAudio() {
    if (!this.audio) this.audio = new (window.AudioContext || window.webkitAudioContext)();
    if (this.audio.state === 'suspended') this.audio.resume();
    return this.audio;
  }

  play() {
    this.stop();
    if (!this.samples.length) return;
    const ctx = this.ensureAudio();
    const buf = ctx.createBuffer(1, this.samples.length, this.rate);
    buf.getChannelData(0).set(this.samples);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
    this.source = src;
  }

  stop() {
    try { this.source?.stop(); } catch {}
    this.source = null;
    if (this.rec) {
      this.rec.stop();
      this.rec = null;
    }
  }

  async record() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const rec = new MediaRecorder(stream);
    const chunks = [];
    rec.ondataavailable = (e) => chunks.push(e.data);
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks);
      const ctx = this.ensureAudio();
      const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
      this.samples = buf.getChannelData(0).slice();
      this.rate = buf.sampleRate;
      this.commit();
      this.draw();
      this.info();
    };
    rec.start();
    this.rec = rec;
    document.getElementById('sound-info').textContent = 'Recording… click Stop';
  }

  snapshot() {
    this.undo.push({ samples: this.samples.slice(), rate: this.rate });
    if (this.undo.length > 20) this.undo.shift();
  }

  effect(name) {
    if (name === 'undo') {
      const last = this.undo.pop();
      if (last) {
        this.samples = last.samples;
        this.rate = last.rate;
        this.commit();
        this.draw();
        this.info();
      }
      return;
    }
    this.snapshot();
    let s = this.samples;
    if (name === 'faster') s = resample(s, 1 / 1.12);
    if (name === 'slower') s = resample(s, 1.12);
    if (name === 'louder') s = s.map((x) => Math.max(-1, Math.min(1, x * 1.25)));
    if (name === 'softer') s = s.map((x) => x * 0.8);
    if (name === 'mute') s = s.map(() => 0);
    if (name === 'fade-in') s = s.map((x, i) => x * (i / s.length));
    if (name === 'fade-out') s = s.map((x, i) => x * (1 - i / s.length));
    if (name === 'reverse') s = Float32Array.from(s).reverse();
    if (name === 'echo') {
      const out = new Float32Array(s.length);
      const d = Math.floor(this.rate * 0.18);
      for (let i = 0; i < s.length; i++) out[i] = s[i] + (i >= d ? s[i - d] * 0.45 : 0);
      s = out;
    }
    if (name === 'robot') {
      s = s.map((x, i) => x * Math.sign(Math.sin(i * 0.25)));
    }
    if (name === 'trim') {
      let a = 0, b = s.length - 1;
      while (a < b && Math.abs(s[a]) < 0.01) a++;
      while (b > a && Math.abs(s[b]) < 0.01) b--;
      s = s.slice(a, b + 1);
    }
    this.samples = s instanceof Float32Array ? s : Float32Array.from(s);
    this.commit();
    this.draw();
    this.info();
  }

  commit() {
    const t = this.app.current;
    if (!t) return;
    const old = this.sound();
    const name = old?.name || 'sound';
    const wav = encodeWav(this.samples, this.rate);
    const next = soundFromWav(name, wav, this.rate);
    const idx = t._soundIndex || 0;
    if (t.sounds[idx]) t.sounds[idx] = next;
    else t.sounds.push(next);
    this.app.renderSoundList();
  }
}

function resample(samples, factor) {
  const n = Math.max(1, Math.floor(samples.length * factor));
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = i / factor;
    const j = Math.floor(x);
    const f = x - j;
    const a = samples[j] || 0;
    const b = samples[j + 1] || 0;
    out[i] = a + (b - a) * f;
  }
  return out;
}

export function newPopSound() {
  return soundFromWav('pop', popSound(), 22050);
}
export function newMeowSound() {
  return soundFromWav('meow', meowSound(), 22050);
}
